import React, { useState, useEffect, useRef } from "react";
import { getSystemPrompts, upsertSystemPrompts, deleteSystemPrompt, getLatestTrainingFile } from "../../../config/apiConfig";
import { AgGridReact } from "ag-grid-react";
import { sbaAxios } from "../../utils/sbaAxios";
import { Modal, Form} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import * as XLSX from "xlsx";
import { SBAComboBox } from "../../UiComponents/SBADropDown";

export const SystemPrompts = () => {
  const [gridData, setGridData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [modalMode, setModalMode] = useState("add"); // "add", "edit", "delete"
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [sortOrderList, setSortOrderList] = useState([]);
  const [latestQuestions, setLatestQuestions] = useState([]);
  const [selectedLateQuestion, setSelectedLateQuestion] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api); // Store grid API when grid is ready
};

  const fetchSystemPrompts = async () => {
    try {
      const response = await sbaAxios({
        url: `${getSystemPrompts}`,
        method: "get",
      });
        setGridData(response.sort((a,b) => a.SortOrder - b.SortOrder));
        setSortOrderList(response.map((item) => item.SortOrder))
    } catch (e) {
      console.log(e, "Error Fetching the view data");
      setGridData([]);
      setSortOrderList([])
    }
  };

  const fetchLatestTrainingFile = async () => {
    try {
        const response = await sbaAxios({
          url: getLatestTrainingFile
        });
        
        if (response && response.FileName && response.FileData) {
            const { FileName, FileData } = response;

            // Convert byte array (base64 string) to Blob
            const byteCharacters = atob(FileData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

            // Read the Excel file
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                // Convert Excel data into AG Grid format
                const formattedData = jsonData.slice(1).map((row, index) => ({
                    id: index+1,
                    question: row[0] || "",
                    query: row[1] || ""
                }));

                setLatestQuestions(formattedData);
            };

            reader.readAsArrayBuffer(blob);
        } else {
            toast.error("No file data found.");
        }
    } catch (error) {
        console.error("Error fetching latest training file:", error);
        toast.error("Failed to load training file.");
    }
};

  useEffect(() => {
    fetchLatestTrainingFile();
    fetchSystemPrompts();
  }, []);

  // Handle Add button click
  const handleAddClick = () => {
    setModalMode("add");
    setPrompt("");
    setSelectedPrompt(null);
    setSelectedLateQuestion(null);
    setShowModal(true);
  };

  // Handle Edit button click
  const handleEditClick = (params) => {
    setModalMode("edit");
    setPrompt(params.data.Prompt);
    setSelectedPrompt(params.data);
    setSelectedLateQuestion(()=>{
      const foundQuestion = latestQuestions.find((item) => item.question === params.data.Prompt);
      return foundQuestion || null;
    })
    setShowModal(true);
  };

  // Handle Delete button click
  const handleDeleteClick = (params) => {
    setModalMode("delete");
    setSelectedPrompt(params.data);
    setShowModal(true);
  };

  // Handle Save (Add/Edit)
  const handleSave = async () => {
    const highestSortOrder = Math.max(...gridData.map(item => item.SortOrder));
    const postData = modalMode === "add" ? [
        {
            promptId: 0,
            prompt: prompt,
            sortOrder: highestSortOrder+1,
            isShared: true,
            isVisible: true
        }
    ]:[
        {
            promptId: selectedPrompt.PromptId,
            prompt: prompt,
            sortOrder: selectedPrompt.SortOrder,
            isShared: selectedPrompt.IsShared,
            isVisible: selectedPrompt.IsVisible
        }
    ];
    try {
      await sbaAxios({
        url: upsertSystemPrompts,
        method: "post",
        data: postData
      });
      setShowModal(false);
      fetchSystemPrompts(); // Refresh data
      toast.success(`Prompt ${modalMode === "add"? "added": "updated"} successfully!`)
    } catch (e) {
        toast.error(`Unable to  ${modalMode === "add"? "add": "update"} prompt`)
    }
  };

  // Handle Delete Confirmation
  const handleConfirmDelete = async () => {
    try {
      await sbaAxios({
        url: `${deleteSystemPrompt}/${selectedPrompt.PromptId}`,
        method: "post",
      });
      setShowModal(false);
      toast.success("Prompt deleted successfully!")
      fetchSystemPrompts(); // Refresh data
    } catch (e) {
        toast.error("Unable to delete prompt")
    }
  };

  const handleSorting = async(updatedSort) => {
    const postData = updatedSort.map((item) => ({
        promptId: item.PromptId,
        prompt: item.Prompt,
        sortOrder: item.SortOrder,
        isShared: item.IsShared,
        isVisible: item.IsVisible
    }))
    try {
      await sbaAxios({
        url: upsertSystemPrompts,
        method: "post",
        data: postData
      });
      fetchSystemPrompts(); // Refresh data
    } catch (e) {
    }
  }

  const actionCellRenderer = (params) => {
    return (
      <div className="actions-cell">
        <i className="fas fa-edit" style={{ color:"#47A547", margin: "0 8px", cursor: "pointer" }} onClick={() => handleEditClick(params)}></i>
        <i className="fa fa-trash" style={{ color: "red" , margin: "0 8px", cursor: "pointer" }} onClick={() => handleDeleteClick(params)}></i>
      </div>
    );
  };


    const colDefs = [
        { 
            headerName: "", 
            field: "drag", 
            rowDrag: true, // Enables row dragging
            width: 50, 
            sortable: false, 
            suppressMovable: true 
          },
      { headerName: "Prompt", field: "Prompt", flex: 1},
      { headerName: "Action", field: "Action", cellRenderer: actionCellRenderer,flex: 0.1}
    ];

  const defaultColDef = {
    sortable: true,
    filter: "agTextColumnFilter",
    resizable: true,
  };

  const onRowDragEnd = (event) => {
    if (!gridApi || !event.overNode) return; // Ensure API exists

    // Get the latest row data from the grid
    let newData = [];
    gridApi.forEachNode((node) => {
        newData.push({ ...node.data }); // Clone latest data
    });

    console.log('after re order: ', newData)

    const updatedRecords = newData.map((item, index) => ({
        ...item,
        SortOrder:sortOrderList[index]
    }))
    console.log("Updated sort order:", updatedRecords);
    handleSorting(updatedRecords);
};

  return (
    <>
        <div className="container-fluid">
          {/* Dropdown for Grouping */}
          <div className="row mb-2">
          <div className="col d-flex justify-content-end align-items-center">
                    <button className="p-sm-btn mb-2" onClick={handleAddClick}>
                      Add System Prompt
                    </button>
              </div>
          </div>

          <div className="ag-theme-alpine m-1" style={{ height: "700px" }}>
            <AgGridReact
              rowData={gridData}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              rowDragManaged={true} // Enables built-in row reordering
              animateRows={true}
              onRowDragEnd={onRowDragEnd} // Calls function when dragging ends
              onGridReady={onGridReady}
            />
          </div>

          {/* Modal */}
          <Modal show={showModal} onHide={() => setShowModal(false)} dialogClassName="modal-md">
                <Modal.Header closeButton>
                <Modal.Title>
                    {modalMode === "add" && "Add System Prompt"}
                    {modalMode === "edit" && "Edit System Prompt"}
                    {modalMode === "delete" && "Delete System Prompt"}
                </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                {modalMode === "delete" ? (
                    <p>Are you sure you want to delete this prompt?</p>
                ) : (
                    <Form>
                    <Form.Group>
                        <Form.Label>Prompt</Form.Label>
                        <div>
                        <SBAComboBox
                            className="w-100"
                            data={latestQuestions}
                            textField={"question"}
                            valueField={"id"}
                            value={selectedLateQuestion}
                            onChange={(e) => {
                              setSelectedLateQuestion(e.value)
                              setPrompt(e.value?.question)
                            }}
                            popupSettings={{ className: "modalDD" }}
                        ></SBAComboBox>
                    </div>
                    </Form.Group>
                    </Form>
                )}
                </Modal.Body>
                <Modal.Footer>
                <button className="s-sm-btn mx-1" onClick={() => setShowModal(false)}>Cancel</button>
                {modalMode === "delete" ? (
                    <button className="p-sm-btn mx-1" onClick={handleConfirmDelete}>Delete</button>
                ) : (
                    <button className="p-sm-btn mx-1" onClick={handleSave}>Save</button>
                )}
                </Modal.Footer>
            </Modal>
        </div>
        <ToastContainer/>
    </>
  );
};
